import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import {
  RBAC_APPS
} from '../constant/rbacConfig';
import { RBAC_ENABLED } from '../constant/globalConstants';
import { ROLE_ACCESS, ROUTES } from '../constant/keyConstants';
import AccessDeniedModal from '../components/AccessDeniedModal/AccessDeniedModal';

/**
 * Custom hook to access RBAC (Role-Based Access Control) data from Redux store
 * 
 * @example
 * // Basic usage with constants (recommended)
 * import { RBAC_APPS, UplGenieFeatures } from '../constant/rbacConfig';
 * const { hasFeature, hasFeatureByKey } = useRbac();
 * 
 * // Check if a feature is enabled using constant
 * if (hasFeatureByKey(RBAC_APPS.CHAT_GENIE, UplGenieFeatures.DOC_UPLOAD)) {
 *   // Show upload button
 * }
 * 
 * // Or using direct feature name
 * if (hasFeature('doc_upload', RBAC_APPS.CHAT_GENIE)) {
 *   // Show upload button
 * }
 * 
 * // Get Chat Genie specific data
 * const { uplGenieLanguages, uplGenieCountries, uplGenieRole } = useRbac();
 * 
 * // Access other apps
 * const { getAppData, getUserRole } = useRbac();
 * const translationData = getAppData(RBAC_APPS.TRANSLATION);
 * 
 * @returns {Object} Object containing RBAC data and utility functions
 */
export const useRbac = () => {
  const rbacData = useSelector((state) => state.state?.rbacData);

  // Get Chat Genie specific data (extracted from rbacData)
  const uplGenieData = useMemo(() => {
    return rbacData?.[RBAC_APPS.CHAT_GENIE] || null;
  }, [rbacData]);

  // Get app data by app name
  const getAppData = (appName) => {
    // If RBAC is disabled, return a mock app object to allow access
    if (!RBAC_ENABLED) {
      return { is_enabled: true, roles: { Default: { features: {} } } };
    }
    if (!rbacData) return null;
    return rbacData[appName] || null;
  };

  // Get user's role for a specific app
  const getUserRole = (appName = RBAC_APPS.CHAT_GENIE) => {
    const appData = appName === RBAC_APPS.CHAT_GENIE ? uplGenieData : getAppData(appName);
    if (!appData || !appData.roles) return null;

    // Return the first role found (assuming user has one role per app)
    // You can modify this logic based on your actual role assignment
    return Object.keys(appData.roles)[0] || null;
  };

  // Check if a feature is enabled for the user's role in an app
  const hasFeature = (featureName, appName = RBAC_APPS.CHAT_GENIE, roleName = null) => {
    // If RBAC is disabled globally, allow all features
    if (!RBAC_ENABLED) return true;

    const appData = appName === RBAC_APPS.CHAT_GENIE ? uplGenieData : getAppData(appName);
    if (!appData || !appData.roles) return false;

    // If roleName is provided, use it; otherwise get user's role
    const role = roleName || getUserRole(appName);
    if (!role || !appData.roles[role]) return false;

    return appData.roles[role].features?.[featureName] === true;
  };

  // Check if a feature is enabled using feature value from constants (type-safe)
  // Accepts the feature value directly (e.g., UplGenieFeatures.DOC_UPLOAD which is 'doc_upload')
  const hasFeatureByKey = (appName, featureValue) => {
    // featureValue is already the actual feature name (e.g., 'doc_upload')
    // Just use it directly with hasFeature
    return hasFeature(featureValue, appName);
  };

  // Check if user has any of the specified features
  const hasAnyFeature = (featureNames, appName = RBAC_APPS.CHAT_GENIE, roleName = null) => {
    if (!Array.isArray(featureNames) || featureNames.length === 0) return false;
    return featureNames.some(featureName => hasFeature(featureName, appName, roleName));
  };

  // Check if user has all of the specified features
  const hasAllFeatures = (featureNames, appName = RBAC_APPS.CHAT_GENIE, roleName = null) => {
    if (!Array.isArray(featureNames) || featureNames.length === 0) return false;
    return featureNames.every(featureName => hasFeature(featureName, appName, roleName));
  };

  // Get all features for a role in an app
  const getFeatures = (appName = RBAC_APPS.CHAT_GENIE, roleName = null) => {
    const appData = appName === RBAC_APPS.CHAT_GENIE ? uplGenieData : getAppData(appName);
    if (!appData || !appData.roles) return {};

    const role = roleName || getUserRole(appName);
    if (!role || !appData.roles[role]) return {};

    return appData.roles[role].features || {};
  };

  // Get enabled features only (filter out disabled features)
  const getEnabledFeatures = (appName = RBAC_APPS.CHAT_GENIE, roleName = null) => {
    const allFeatures = getFeatures(appName, roleName);
    return Object.entries(allFeatures)
      .filter(([_, enabled]) => enabled === true)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  };

  // Memoize Chat Genie enabled features to prevent infinite loops
  const uplGenieEnabledFeatures = useMemo(() => {
    if (!uplGenieData || !uplGenieData.roles) return {};

    // Get the first role (assuming user has one role per app)
    const role = Object.keys(uplGenieData.roles)[0] || null;
    if (!role || !uplGenieData.roles[role]) return {};

    const allFeatures = uplGenieData.roles[role].features || {};
    // Filter to only enabled features (true values)
    return Object.entries(allFeatures)
      .filter(([_, enabled]) => enabled === true)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  }, [uplGenieData]);

  // Get available languages for an app
  const getLanguages = (appName = RBAC_APPS.CHAT_GENIE) => {
    const appData = appName === RBAC_APPS.CHAT_GENIE ? uplGenieData : getAppData(appName);
    return appData?.languages || [];
  };

  // Get available countries for an app
  const getCountries = (appName = RBAC_APPS.CHAT_GENIE) => {
    const appData = appName === RBAC_APPS.CHAT_GENIE ? uplGenieData : getAppData(appName);
    return appData?.countries || [];
  };

  // Get app ID
  const getAppId = (appName = RBAC_APPS.CHAT_GENIE) => {
    const appData = appName === RBAC_APPS.CHAT_GENIE ? uplGenieData : getAppData(appName);
    return appData?.app_id || null;
  };

  // Get scope for an app
  const getScope = (appName = RBAC_APPS.CHAT_GENIE) => {
    const appData = appName === RBAC_APPS.CHAT_GENIE ? uplGenieData : getAppData(appName);
    return appData?.scope || null;
  };

  // Get all roles available for an app
  const getAvailableRoles = (appName = RBAC_APPS.CHAT_GENIE) => {
    const appData = appName === RBAC_APPS.CHAT_GENIE ? uplGenieData : getAppData(appName);
    if (!appData || !appData.roles) return [];
    return Object.keys(appData.roles);
  };

  // Shared: app → route mapping
  const APP_ROUTE_MAP = {
    [RBAC_APPS.CHAT_GENIE]:   ROUTES.CHAT,
    [RBAC_APPS.PROCUREMENT]:  ROUTES.PROCUREMENT_AI,
    [RBAC_APPS.TRANSLATION]:  ROUTES.TRANSLATE,
    [RBAC_APPS.GMT]:          ROUTES.GMT_CHAT,
    [RBAC_APPS.HR_ASSISTANT]: ROUTES.CHAT,
    [RBAC_APPS.DEEP_INSIGHTS]: ROUTES.DEEP_INSIGHTS_CHAT,
  };
  const normalizeRole = (r) => (r ? r.toUpperCase().replace(/\s+/g, '') : '');

  // Compute landing route from raw RBAC data — Deep Insights Super Admin gets priority.
  // Used right after login (before Redux store updates) via Login.jsx.
  const getRouteFromRbacData = (rbacObj) => {
    if (!rbacObj) return ROUTES.CHAT;
    const diData = rbacObj[RBAC_APPS.DEEP_INSIGHTS];
    if (diData) {
      const role = diData.role_name || Object.keys(diData.roles || {})[0];
      if (normalizeRole(role) === ROLE_ACCESS.SUPERADMIN) return ROUTES.DEEP_INSIGHTS_CHAT;
    }
    const firstApp = Object.values(RBAC_APPS).find(appName => !!rbacObj[appName]);
    return APP_ROUTE_MAP[firstApp] || ROUTES.CHAT;
  };

  // Get the first accessible route based on RBAC apps in Redux state.
  // Used by LandingRedirect in AuthGaurd.jsx.
  const getFirstAccessibleRoute = (excludeApp = null) => {
    if (!RBAC_ENABLED) {
      const matchingRoute = Object.values(APP_ROUTE_MAP).find(route => window.location.pathname.startsWith(route));
      return matchingRoute || ROUTES.CHAT;
    }
    if (!rbacData) return ROUTES.CHAT;

    // Deep Insights Super Admin → priority route
    const diData = getAppData(RBAC_APPS.DEEP_INSIGHTS);
    if (diData && excludeApp !== RBAC_APPS.DEEP_INSIGHTS) {
      const role = getUserRole(RBAC_APPS.DEEP_INSIGHTS) || diData?.role_name;
      if (normalizeRole(role) === ROLE_ACCESS.SUPERADMIN) return ROUTES.DEEP_INSIGHTS_CHAT;
    }

    const firstApp = Object.values(RBAC_APPS).find(appName => appName !== excludeApp && !!getAppData(appName));
    return APP_ROUTE_MAP[firstApp] || (excludeApp ? null : ROUTES.VIDEO_TUTORIALS);
  };

  // Check access to an app (returns boolean)
  const checkAccess = (appName) => {
    if (!RBAC_ENABLED) return true;
    return !!getAppData(appName);
  };

  // Returns the AccessDeniedModal element if the user does not have access
  // to the given app, otherwise returns null. The fallback route is
  // automatically computed to the next accessible app.
  const renderAccessDenied = (appName) => {
    if (!appName) return null;
    if (checkAccess(appName)) return null;
    const fallbackRoute = getFirstAccessibleRoute(appName);
    return (
      <AccessDeniedModal
        appName={appName}
        fallbackRoute={fallbackRoute}
      />
    );
  };

  return {
    // Raw data
    rbacData,
    uplGenieData,

    // RBAC status
    isRbacEnabled: RBAC_ENABLED,
    isRbacLoaded: !RBAC_ENABLED || !!rbacData,

    // Utility functions
    getAppData,
    getUserRole,
    hasFeature,
    hasFeatureByKey,
    hasAnyFeature,
    hasAllFeatures,
    getFeatures,
    getEnabledFeatures,
    getLanguages,
    getCountries,
    getAppId,
    getScope,
    getAvailableRoles,
    getFirstAccessibleRoute,
    getRouteFromRbacData,
    checkAccess,
    renderAccessDenied,

    // Convenience getters for Chat Genie (most common use case)
    uplGenieRole: getUserRole(RBAC_APPS.CHAT_GENIE),
    uplGenieFeatures: getFeatures(RBAC_APPS.CHAT_GENIE),
    uplGenieEnabledFeatures,
    uplGenieLanguages: getLanguages(RBAC_APPS.CHAT_GENIE),
    uplGenieCountries: getCountries(RBAC_APPS.CHAT_GENIE),
    uplGenieAppId: getAppId(RBAC_APPS.CHAT_GENIE),
    uplGenieScope: getScope(RBAC_APPS.CHAT_GENIE),
    uplGenieAvailableRoles: getAvailableRoles(RBAC_APPS.CHAT_GENIE),
  };
};

